// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { from, lastValueFrom, of } from 'rxjs';
import { catchError, mergeMap, toArray } from 'rxjs/operators';
import { NetworkService } from '@core/services/network.service';
import { LoggerService } from '@core/services/logger.service';
import { FeatureFlagsStore } from '@core/store/feature-flags.store';
import { OfflineStore } from './offline-store.service';

const LIST_KEY_LIKE = '/api/pwi/flights?%';
const DETAIL_KEY_LIKE = '/api/pwi/flights/%/detail';
const DETAIL_KEY_RE = /^\/api\/pwi\/flights\/(\d+)\/detail$/;
const TYPES_KEY = '/api/pwi/crew-match-errors/types';
/** The window the list snapshots define (EF-2.1: 7 days). A list older than
 *  this no longer describes the recent window and stops protecting flights. */
const WINDOW_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Parallel detail fetches during prefetch — polite, and the whole window is
 *  ~15 × 30 kB anyway. */
const PREFETCH_CONCURRENCY = 3;

/**
 * B2.5 + B4 in one reconciliation — "the vault mirrors the listed window":
 *
 *  - PREFETCH (EF-2.1, the "listés" half B2 alone doesn't cover): when a fresh
 *    flight list is captured, fetch the details the vault is missing. The
 *    requests are ordinary HttpClient GETs, so the B2 interceptor captures the
 *    responses — this service stores NOTHING itself.
 *  - RETENTION (EF-8.1): list snapshots older than the 7-day window are
 *    dropped; a detail row survives only while its flight appears in at least
 *    one remaining list snapshot (the union across snapshots keeps one
 *    fleet's reconciliation from evicting another fleet's flights). The
 *    window is thus anchored on the backend's own list query — "7 days from
 *    the current date" — with no flight-date parsing in the storage layer.
 *  - Eviction only ever runs here, i.e. on a FRESH list while online: nothing
 *    is ever evicted while offline, when the vault is all the pilot has.
 *
 * Exemptions still stubbed: pending actions (phase C) and downloaded/pinned
 * content (phase D) will be excluded from eviction when they exist.
 *
 * Triggered by the offline-cache interceptor on every flight-LIST capture —
 * which already implies: flag on, online, real user, not impersonating.
 */
@Injectable({ providedIn: 'root' })
export class OfflineWindowSyncService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(OfflineStore);
  private readonly network = inject(NetworkService);
  private readonly flags = inject(FeatureFlagsStore);
  private readonly logger = inject(LoggerService);
  private syncing = false;

  /** Called by the offline-cache interceptor when a flight-list capture lands. */
  onListCaptured(pilotKey: string, listBody: unknown): void {
    if (this.syncing) return;
    this.syncing = true;
    void this.reconcile(pilotKey, listBody).finally(() => {
      this.syncing = false;
    });
  }

  private async reconcile(pilotKey: string, listBody: unknown): Promise<void> {
    // Conditions can flip between the capture and this pass running.
    if (!this.flags.isEnabled('OfflineMode') || !this.network.isOnline()) return;

    const currentIds = this.extractIds(listBody);

    // 1) Stale list snapshots stop defining the window.
    await this.store.deleteReadsOlderThan(pilotKey, LIST_KEY_LIKE, Date.now() - WINDOW_TTL_MS);

    // 2) The protected set: every flight still present in ANY remaining list
    //    snapshot (multi-fleet safety), plus the fresh list itself.
    const lists = await this.store.getReadsMatching(pilotKey, LIST_KEY_LIKE);
    const protectedIds = new Set<number>(currentIds);
    for (const list of lists) {
      for (const id of this.extractIds(list.body)) protectedIds.add(id);
    }

    // 3) Evict details whose flight left the window.
    //    (Phase C/D exemptions — pending actions, downloads — plug in here.)
    const details = await this.store.getReadsMatching(pilotKey, DETAIL_KEY_LIKE);
    let evicted = 0;
    const have = new Set<number>();
    for (const detail of details) {
      const id = this.idFromDetailKey(detail.cacheKey);
      if (id === null) continue;
      if (protectedIds.has(id)) {
        have.add(id);
      } else {
        await this.store.deleteRead(pilotKey, detail.cacheKey);
        evicted++;
      }
    }

    // 4) Prefetch the missing details of the CURRENT window. Responses flow
    //    through the interceptor, which captures them — no storage here. A
    //    failed fetch just stays missing; the next list load retries.
    const missing = currentIds.filter((id) => !have.has(id));
    await lastValueFrom(
      from(missing).pipe(
        mergeMap(
          (id) => this.http.get(`/api/pwi/flights/${id}/detail`).pipe(catchError(() => of(null))),
          PREFETCH_CONCURRENCY,
        ),
        toArray(),
      ),
      { defaultValue: [] },
    );

    // 5) The static types list the detail view needs — once.
    const types = await this.store.getReadsMatching(pilotKey, TYPES_KEY);
    if (types.length === 0) {
      await lastValueFrom(this.http.get(TYPES_KEY).pipe(catchError(() => of(null))), { defaultValue: null });
    }

    this.logger.info('OfflineWindowSync', `window sync: +${missing.length} details requested, -${evicted} evicted`);
  }

  /** Flight ids from a cached list body ({ data: FlightListItemDto[] } envelope, defensive). */
  private extractIds(body: unknown): number[] {
    const data = (body as { data?: unknown } | null)?.data ?? body;
    if (!Array.isArray(data)) return [];
    return data
      .map((item) => (item as { id?: unknown } | null)?.id)
      .filter((id): id is number => typeof id === 'number');
  }

  private idFromDetailKey(cacheKey: string): number | null {
    const match = DETAIL_KEY_RE.exec(cacheKey);
    return match ? Number(match[1]) : null;
  }
}
